import { Flame, Info } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { AutomationView } from "../../../../lib/marketingAutomation";
import { Bar, Note, Panel, Stat, dt, num } from "../shared";

/**
 * Where the sending domain stands today.
 *
 * TWO NUMBERS THAT LOOK THE SAME AND ARE NOT. The ceiling is what the sender
 * may send today across every campaign; the campaign's own daily cap is a
 * different number on a different page. Three campaigns each politely under a
 * cap of 200 are one domain sending 600, and that arithmetic is the reason this
 * card exists at all.
 *
 * THE RAMP IS SHOWN AS A POSITION, NOT AS A SETTING. There is no field to type
 * a bigger number into, because a ramp somebody can shorten by typing is the
 * operator discipline it was built to replace. The only control is whether the
 * ramp applies, and that is on the schedule card beside the rest of the switches.
 *
 * ⚠ The card says out loud that the ramp restarts when the sending account
 * changes. That is the behaviour most likely to surprise somebody - a fresh
 * Resend account drops the ceiling back to day one - and a surprise nobody was
 * told about reads as a bug.
 */
export function RampCard({ view }: { view: AutomationView }) {
  const t = useT();
  const { ramp, today, sender } = view;

  return (
    <Panel
      title={t("marketing.auto.rampTitle")}
      description={t("marketing.auto.rampIntro")}
      icon={<Flame className="size-4" />}
    >
      {!sender.configured ? (
        // No channel means no sender to measure. Showing day one of a ramp for
        // nobody would be a number about nothing.
        <Note tone="warn" icon={<Info className="size-3.5" />}>{t("marketing.auto.noSender")}</Note>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t("marketing.auto.sentToday")} value={num(today.sentToday)} />
            <Stat label={t("marketing.auto.ceiling")} value={num(today.ceiling)} />
            <Stat label={t("marketing.auto.left")} value={num(today.left)} />
            <Stat
              label={t("marketing.auto.binding.label")}
              value={t(`marketing.auto.binding.${today.binding}`)}
            />
          </div>

          <div className="mt-3.5">
            <Bar
              value={today.sentToday}
              total={Math.max(today.ceiling, 1)}
              tone={today.left === 0 ? "warn" : "neutral"}
            />
          </div>

          {ramp ? (
            <div className="mt-3.5 space-y-2 border-t border-border pt-3.5">
              <p className="text-xs text-muted-foreground">
                {t("marketing.auto.rampDay", {
                  day: ramp.dayIndex + 1,
                  days: ramp.days,
                  started: dt(ramp.startedAt),
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {ramp.ceiling === null
                  ? t("marketing.auto.rampOver")
                  : t("marketing.auto.rampToday", { ceiling: num(ramp.ceiling), start: num(ramp.start) })}
              </p>
              <Note tone="info" icon={<Info className="size-3.5" />}>{t("marketing.auto.rampRestartNote")}</Note>
            </div>
          ) : (
            <Note tone="warn" className="mt-3.5" icon={<Info className="size-3.5" />}>
              {t("marketing.auto.rampOff")}
            </Note>
          )}
        </>
      )}
    </Panel>
  );
}
